import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
  Divider,
} from '@mui/material';
import { Article, Business, ContactSupport, Policy, Save } from '@mui/icons-material';

const defaultPages = {
  about: {
    title: 'About Us',
    whoWeAre: '',
    mission: '',
    contactEmail: '',
    companyName: '',
    version: '',
  },
  terms: {
    title: 'Terms & Conditions',
    content: '',
    lastUpdated: '',
  },
  privacy: {
    title: 'Privacy Policy',
    content: '',
    lastUpdated: '',
  },
};

const DriverCmsSettings = () => {
  const [pages, setPages] = useState(defaultPages);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const loadPages = useCallback(async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const { data } = await axios.get('/admin/settings/driver-cms');
      if (data.success) {
        setPages({
          about: { ...defaultPages.about, ...data.pages?.about },
          terms: { ...defaultPages.terms, ...data.pages?.terms },
          privacy: { ...defaultPages.privacy, ...data.pages?.privacy },
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to load driver CMS content',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  const updatePage = (section, field, value) => {
    setPages((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [field]: value,
      },
    }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const { data } = await axios.post('/admin/settings/driver-cms', { pages });
      setPages({
        about: { ...defaultPages.about, ...data.pages?.about },
        terms: { ...defaultPages.terms, ...data.pages?.terms },
        privacy: { ...defaultPages.privacy, ...data.pages?.privacy },
      });
      setMessage({ type: 'success', text: data.message || 'Driver CMS content saved' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to save driver CMS content',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#116611' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Business sx={{ color: '#116611', fontSize: 32 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
              Driver App CMS
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Edit the About, Mission, Terms, and Privacy Policy content shown inside the driver app.
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSave}>
        <Grid container spacing={3}>
          {/* ABOUT SECTION */}
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Business sx={{ color: '#116611' }} />
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  About Us & Mission
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Page Title"
                    value={pages.about.title}
                    onChange={(e) => updatePage('about', 'title', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    value={pages.about.companyName}
                    onChange={(e) => updatePage('about', 'companyName', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Contact Email"
                    value={pages.about.contactEmail}
                    onChange={(e) => updatePage('about', 'contactEmail', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="App Version"
                    value={pages.about.version}
                    onChange={(e) => updatePage('about', 'version', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Who We Are"
                    value={pages.about.whoWeAre}
                    onChange={(e) => updatePage('about', 'whoWeAre', e.target.value)}
                    multiline
                    minRows={3}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Our Mission"
                    value={pages.about.mission}
                    onChange={(e) => updatePage('about', 'mission', e.target.value)}
                    multiline
                    minRows={3}
                    required
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* TERMS SECTION */}
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 3, height: '100%', borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Article sx={{ color: '#116611' }} />
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Terms & Conditions
                </Typography>
              </Stack>
              <TextField
                fullWidth
                label="Section Title"
                value={pages.terms.title}
                onChange={(e) => updatePage('terms', 'title', e.target.value)}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Last Updated"
                value={pages.terms.lastUpdated}
                onChange={(e) => updatePage('terms', 'lastUpdated', e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Content"
                value={pages.terms.content}
                onChange={(e) => updatePage('terms', 'content', e.target.value)}
                margin="normal"
                multiline
                minRows={10}
                required
              />
            </Paper>
          </Grid>

          {/* PRIVACY SECTION */}
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 3, height: '100%', borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Policy sx={{ color: '#116611' }} />
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Privacy Policy
                </Typography>
              </Stack>
              <TextField
                fullWidth
                label="Section Title"
                value={pages.privacy.title}
                onChange={(e) => updatePage('privacy', 'title', e.target.value)}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Last Updated"
                value={pages.privacy.lastUpdated}
                onChange={(e) => updatePage('privacy', 'lastUpdated', e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Content"
                value={pages.privacy.content}
                onChange={(e) => updatePage('privacy', 'content', e.target.value)}
                margin="normal"
                multiline
                minRows={10}
                required
              />
            </Paper>
          </Grid>
        </Grid>

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
          >
            {saving ? 'Saving...' : 'Save Driver CMS Content'}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default DriverCmsSettings;
